import React, { useRef } from 'react';
import { TEMP_MIN, TEMP_MAX, ARC_DEG, clampTemp, tempState, angleForTemp, tempForAngle, tickColor } from '../lib/cabin';

// The cabin temperature knob: a ring of tick marks (blue through white to orange) with the temperatures 62° to 78° around it, a dark brushed-metal knob in the
// middle with the big number, "FAHRENHEIT" and an orange indicator mark. Drag or turn the knob, tap the round − and +, or use the keyboard: 1° steps, 62° to 78°.
const SIZE = 340; const C = SIZE / 2;
const R_OUT = 134; const R_MAJOR = 121; const R_MINOR = 127; const R_LABEL = 154;
const pt = (deg, r) => { const a = (deg * Math.PI) / 180; return [C + r * Math.sin(a), C - r * Math.cos(a)]; };
const DEGREES = Array.from({ length: TEMP_MAX - TEMP_MIN + 1 }, (_, i) => TEMP_MIN + i);

const roundBtn = 'w-12 h-12 rounded-full border border-white/25 text-2xl leading-none text-white hover:bg-white/[0.08] hover:border-white/60 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:border-white/25 transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/70';

export default function TempDial({ value, onChange }) {
  const t = clampTemp(value);
  const box = useRef(null);
  const angle = angleForTemp(t);
  const set = (n) => onChange(clampTemp(n));
  const fromPointer = (e) => {
    const r = box.current.getBoundingClientRect();
    const dx = e.clientX - (r.left + r.width / 2); const dy = e.clientY - (r.top + r.height / 2);
    set(tempForAngle((Math.atan2(dx, -dy) * 180) / Math.PI));
  };
  const down = (e) => {
    e.preventDefault();
    if (e.currentTarget.setPointerCapture) { try { e.currentTarget.setPointerCapture(e.pointerId); } catch (x) { /* older browsers */ } }
    fromPointer(e);
  };
  const move = (e) => { if (e.buttons === 1) fromPointer(e); };
  const key = (e) => {
    const k = e.key;
    if (k === 'ArrowRight' || k === 'ArrowUp') { e.preventDefault(); set(t + 1); }
    else if (k === 'ArrowLeft' || k === 'ArrowDown') { e.preventDefault(); set(t - 1); }
    else if (k === 'PageUp') { e.preventDefault(); set(t + 2); }
    else if (k === 'PageDown') { e.preventDefault(); set(t - 2); }
    else if (k === 'Home') { e.preventDefault(); set(TEMP_MIN); }
    else if (k === 'End') { e.preventDefault(); set(TEMP_MAX); }
  };
  const state = tempState(t);

  return (
    <div data-testid="temp-dial">
      <p className="text-sm tracking-[0.14em] uppercase text-gray-100">The cabin, set your way</p>
      <p className="text-base text-gray-300 mt-2 leading-relaxed">Sent to your chauffeur with every booking, so the car is ready before you step in.</p>
      <div className="flex items-baseline justify-between mt-6">
        <span className="text-xs tracking-[0.2em] uppercase text-gray-300">Cabin temperature</span>
        <span className="text-base text-gray-50" data-testid="temp-state" aria-live="polite">{state}</span>
      </div>

      <div ref={box} className="relative mx-auto mt-2 w-full max-w-[340px] aspect-square touch-none select-none" onPointerDown={down} onPointerMove={move} data-testid="temp-ring">
        <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="absolute inset-0 w-full h-full" aria-hidden="true">
          {DEGREES.map((d) => {
            const a = angleForTemp(d); const even = d % 2 === 0; const on = d === t;
            const [x1, y1] = pt(a, R_OUT); const [x2, y2] = pt(a, on ? R_MAJOR - 4 : even ? R_MAJOR : R_MINOR);
            return <line key={d} x1={x1} y1={y1} x2={x2} y2={y2} stroke={tickColor(d)} strokeWidth={on ? 3.2 : even ? 2 : 1.4} strokeLinecap="round" opacity={on ? 1 : 0.8} data-testid={`tick-${d}`} />;
          })}
          {DEGREES.filter((d) => d % 2 === 0).map((d) => {
            const [x, y] = pt(angleForTemp(d), R_LABEL);
            return <text key={d} x={x} y={y} textAnchor="middle" dominantBaseline="central" fontSize="13" fontWeight={d === t ? 600 : 400} fill={d === t ? '#FFFFFF' : '#B4BAC4'} data-testid={`tick-label-${d}`}>{d}°</text>;
          })}
        </svg>

        {/* the knob: brushed metal that turns with the temperature */}
        <div className="absolute rounded-full" style={{ inset: '19%', boxShadow: '0 18px 40px -10px rgba(0,0,0,0.85), 0 0 0 2px #0b0c0e, 0 0 0 3px rgba(255,255,255,0.10)' }}>
          <div className="absolute inset-0 rounded-full" data-testid="knob-metal"
            style={{ transform: `rotate(${angle}deg)`, transition: 'transform 220ms cubic-bezier(.2,.8,.2,1)',
              background: 'repeating-radial-gradient(circle at 50% 50%, rgba(255,255,255,0.045) 0 1px, rgba(0,0,0,0.05) 1px 2.2px), conic-gradient(from 0deg, #2a2c31, #4b4e56 12%, #24262a 25%, #50535b 38%, #26282c 50%, #4a4d55 62%, #232529 75%, #4f525a 88%, #2a2c31)' }}>
            <span className="absolute left-1/2 -translate-x-1/2 rounded-full" data-testid="knob-indicator" style={{ top: '5%', width: 5, height: '13%', background: '#F97316', boxShadow: '0 0 10px rgba(249,115,22,0.7)' }} />
          </div>
          <div className="absolute rounded-full pointer-events-none" style={{ inset: '9%', background: 'radial-gradient(circle at 50% 38%, rgba(255,255,255,0.10), rgba(0,0,0,0.55) 78%)', boxShadow: 'inset 0 2px 3px rgba(255,255,255,0.16), inset 0 -6px 14px rgba(0,0,0,0.6)' }} />
          <div role="slider" tabIndex={0} aria-label="Cabin temperature" aria-orientation="horizontal" aria-valuemin={TEMP_MIN} aria-valuemax={TEMP_MAX} aria-valuenow={t}
            aria-valuetext={`${t} degrees Fahrenheit, ${state}`} onKeyDown={key} data-testid="temp-knob"
            className="absolute inset-0 rounded-full flex flex-col items-center justify-center outline-none focus-visible:ring-2 focus-visible:ring-white/70 cursor-grab active:cursor-grabbing">
            <span className="font-semibold text-white leading-none tabular-nums" style={{ fontSize: 'clamp(40px, 11.5vw, 54px)', textShadow: '0 2px 6px rgba(0,0,0,0.7)' }} data-testid="temp-value">{t}°</span>
            <span className="mt-2 text-[11px] font-medium tracking-[0.28em] uppercase" style={{ color: '#F97316' }}>Fahrenheit</span>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mt-3">
        <button type="button" aria-label="One degree cooler" disabled={t <= TEMP_MIN} onClick={() => set(t - 1)} className={roundBtn} data-testid="temp-minus">−</button>
        <button type="button" aria-label="One degree warmer" disabled={t >= TEMP_MAX} onClick={() => set(t + 1)} className={roundBtn} data-testid="temp-plus">+</button>
      </div>
    </div>
  );
}
